import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';

import {
    useGetProtobufQuery,
    useGetScheduleConfigQuery,
    useGetScheduleTemplatesQuery,
} from '@/services/dfpFrontendApi';
import type { ScheduleSummary } from '../../types';
import CodeBlock, { type CodeLanguage } from '../CodeBlock/CodeBlock';

interface InspectorTarget {
    vin: string;
    schedule: ScheduleSummary;
}

interface MessageInspectorModalProps {
    target: InspectorTarget | null;
    onClose: () => void;
}

const MERGED_OPTION_ID = '__merged__';

const MessageInspectorModal = ({ target, onClose }: MessageInspectorModalProps) => {
    const intl = useIntl();
    const [activeTab, setActiveTab] = useState<'config' | 'template' | 'proto'>('config');
    const [selectedVariant, setSelectedVariant] = useState(MERGED_OPTION_ID);
    const { copy, copied } = useClipboard({ timeout: 2000 });

    const isOpen = target !== null;
    const vin = target?.vin ?? '';
    const schedule = target?.schedule;

    const { data: templates, isLoading: templatesLoading } = useGetScheduleTemplatesQuery(
        { vin, index: schedule?.index ?? 0 },
        { skip: !isOpen },
    );

    const { data: configData, isLoading: configLoading } = useGetScheduleConfigQuery(
        { vin, index: schedule?.index ?? 0 },
        { skip: !isOpen },
    );

    const { data: protobuf, isLoading: protobufLoading } = useGetProtobufQuery(
        { cid: schedule?.cid ?? 0, version: schedule?.version ?? 0 },
        { skip: !isOpen },
    );

    if (!isOpen || !schedule) return null;

    const hasMultipleVariants = (templates?.variants.length ?? 0) > 1;

    const variantOptions: SelectOption[] = [
        { id: MERGED_OPTION_ID, label: `Merged result (${templates?.variants.length ?? 0} variants)` },
        ...(templates?.variants.map((v) => ({ id: v.name, label: v.name })) ?? []),
    ];

    const getTemplateContent = (): string => {
        if (!templates) return '';
        if (selectedVariant === MERGED_OPTION_ID) return templates.merged;
        return templates.variants.find((v) => v.name === selectedVariant)?.content ?? '';
    };

    const getConfigContent = (): string => {
        return configData ?? '';
    };

    const getCurrentContent = (): string => {
        switch (activeTab) {
            case 'config':
                return getConfigContent();
            case 'template':
                return getTemplateContent();
            case 'proto':
                return protobuf ?? '';
        }
    };

    const isTabLoading =
        (activeTab === 'config' && configLoading) ||
        (activeTab === 'template' && templatesLoading) ||
        (activeTab === 'proto' && protobufLoading);

    const currentContent = getCurrentContent();

    const tabLanguage: CodeLanguage = activeTab === 'config' ? 'yaml' : activeTab === 'template' ? 'json' : 'protobuf';

    const tabs = [
        {
            id: 'config' as const,
            label: intl.formatMessage({ id: 'intl-msg:messageInspector.executionConfig' }),
            icon: 'icon-detail-view',
        },
        {
            id: 'template' as const,
            label: intl.formatMessage({ id: 'intl-msg:messageInspector.baseTemplate' }),
            icon: 'icon-document',
        },
        {
            id: 'proto' as const,
            label: intl.formatMessage({ id: 'intl-msg:messageInspector.protobuf' }),
            icon: 'icon-files',
        },
    ];

    const variantLabel =
        schedule.variants.length === 1
            ? schedule.variants[0]
            : `${schedule.variants[0]} (+${schedule.variants.length - 1})`;

    const body = (
        <div>
            <div className="display-flex border-bottom border-color-light">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={[
                            'display-flex align-items-center gap-5 padding-x-15 padding-y-10 text-size-12',
                            'text-bold border-bottom-2 cursor-pointer bg-white',
                            'border-left-0 border-right-0 border-top-0',
                            activeTab === tab.id
                                ? 'border-color-primary text-color-primary'
                                : 'border-color-transparent text-color-gray hover-text-color-dark',
                        ].join(' ')}
                    >
                        <span className={`uikit-icon ${tab.icon}`} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'template' && hasMultipleVariants && (
                <div className="padding-10 bg-lightest border-bottom border-color-light">
                    <div className="display-flex align-items-center gap-10">
                        <span className="text-size-12 text-bold text-color-gray white-space-nowrap">
                            <FormattedMessage id="intl-msg:messageInspector.viewing" />
                        </span>
                        <div style={{ width: 350 }}>
                            <Select
                                options={variantOptions}
                                value={[selectedVariant]}
                                onChange={(opt) => setSelectedVariant(opt?.id ?? MERGED_OPTION_ID)}
                                bsSize="sm"
                            />
                        </div>
                    </div>
                </div>
            )}

            {isTabLoading ? (
                <div className="display-flex justify-content-center padding-25">
                    <Spinner />
                </div>
            ) : (
                <CodeBlock code={currentContent} language={tabLanguage} minHeight={300} maxHeight={450} />
            )}
        </div>
    );

    const footer = (
        <Button variant="outline" onClick={() => copy(currentContent)} disabled={isTabLoading}>
            <span className={`uikit-icon ${copied ? 'icon-ok' : 'icon-duplicate'} margin-right-5`} />
            {copied
                ? intl.formatMessage({ id: 'intl-msg:common.copied' })
                : intl.formatMessage({ id: 'intl-msg:messageInspector.copyCurrentTab' })}
        </Button>
    );

    return (
        <Dialog
            show={isOpen}
            onClose={onClose}
            title={
                <span className="display-flex align-items-center gap-10">
                    <span className="uikit-icon icon-layer text-color-primary" />
                    <span>
                        <FormattedMessage id="intl-msg:messageInspector.title" />
                        <span className="text-size-12 text-color-gray font-family-mono margin-left-10">
                            CID {schedule.cid} — {variantLabel}
                        </span>
                    </span>
                </span>
            }
            body={body}
            footer={footer}
            bsSize="lg"
            showCloseButton
        />
    );
};

export default MessageInspectorModal;
